import React, { useState, useEffect } from "react";
import { getMemberInfo, useMembers } from "@/hooks/useMembers";
import MInputField from "@/components/ui/atoms/MInputField";
import MemberPickerDrawer from "@/components/ui/atoms/MemberPickerDrawer";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { FileCategory } from "@/types/document.dto";
import { FileCategoryLabel } from "@/config/document-table.config";
import { mediaService } from "@/services/media.service";
import {
  triggerNativeShare,
  triggerNativeDownload,
  triggerNativeOfflineDoc,
} from "@/utils/nativeBridge";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import MDatePicker from "@/components/ui/molecules/MDatePicker";
import MNavigationButton from "@/components/ui/atoms/MNavigationButton";
import { MToggle } from "@/components/ui/atoms/MToggle";
import MDialog from "@/components/ui/atoms/MDialog";
import styles from "@/components/ui/atoms/MDocumentCard.module.css";
import {
  IconShare3,
  IconMessage2,
  IconStar,
  IconShieldLock,
  IconCloudDownload,
  IconTrash,
} from "@tabler/icons-react";
import { useDocument } from "@/hooks/useDocument";
import { useDocumentSettings } from "@/hooks/useDocumentSettings";
import { DocumentSettings } from "@/types/document-settings";
import { useIsDesktopPWA } from "@/hooks/useIsDesktopPWA";

export interface DocumentDrawerProps {
  open: boolean;
  onClose: () => void;
  displayName: string;
  fileKey?: string; // S3 key for signed url (webview)
  fileUrl?: string; // Add fileUrl for share/download bridge
  fileName?: string; // Add fileName for download bridge
  isImportant?: boolean;
  isPrivate?: boolean;
  availableOffline?: boolean;
  onDownloadClick?: () => void;
  onShareClick?: () => void;
  onCommentClick?: () => void;
  onDelete?: () => void;
  onImportantChange?: (checked: boolean) => Promise<void>;
  onPrivateChange?: (checked: boolean) => Promise<void>;
  onOfflineChange?: (checked: boolean) => Promise<void>;
  commentButtonText?: string;
  category?: number;
  document?: any;
  onUpdate?: () => Promise<void>;
  onDocumentChange?: (updatedDoc: any) => void; // Callback to update parent's document without refresh
}

const DocumentDrawer: React.FC<DocumentDrawerProps> = ({
  open,
  onClose,
  displayName,
  fileKey,
  fileUrl,
  fileName,
  onDownloadClick,
  onShareClick,
  onCommentClick,
  onDelete,
  commentButtonText = "Add Comment",
  category,
  document,
  onUpdate,
  onDocumentChange,
}) => {
  const isDesktopPWA = useIsDesktopPWA();
  // Member logic
  const { getManagedMembers } = useMembers({ autoFetch: false });
  const [members, setMembers] = useState<any[]>([]);
  const memberInfo = getMemberInfo();
  const [assignedTo, setAssignedTo] = useState<any>(null);
  const { updateUncategorizedDocument, updateDocumentFlag } = useDocument(
    memberInfo.id
  );
  const { getSettings, updateFlag } = useDocumentSettings(memberInfo.id);
  const [fileCategory, setFileCategory] = useState<FileCategory | null>(
    category ?? null
  );
  const [showMemberDrawer, setShowMemberDrawer] = useState(false);
  const [showCategoryDrawer, setShowCategoryDrawer] = useState(false);
  const [editableDisplayName, setEditableDisplayName] = useState(
    displayName || ""
  );
  const [displayNameChanged, setDisplayNameChanged] = useState(false);
  const [editableDate, setEditableDate] = useState<Date | undefined>(
    document?.documentDate ? new Date(document.documentDate) : undefined
  );
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Document settings state
  const [settings, setSettings] = useState<DocumentSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Local document state for real-time updates
  const [localDocument, setLocalDocument] = useState<any>(document);

  useEffect(() => {
    if (open) {
      // Update local document when drawer opens or document prop changes
      setLocalDocument(document);

      getManagedMembers().then((membersList) => {
        setMembers(membersList);
        // Initialize assignedTo with actual document owner from document.memberId
        if (document?.memberId) {
          const owner = membersList.find(
            (m: any) => m.id === document.memberId
          );
          if (owner) {
            setAssignedTo(owner);
          }
        }
      });
      setEditableDisplayName(displayName || "");
      setDisplayNameChanged(false);
      setEditableDate(
        document?.documentDate ? new Date(document.documentDate) : undefined
      );
      setFileCategory(category ?? null);

      // Fetch document settings
      if (document?._id || document?.id) {
        setSettingsLoading(true);
        getSettings(document._id || document.id)
          .then((fetchedSettings) => {
            setSettings(fetchedSettings);
          })
          .catch((err) => {
            console.error("Error fetching document settings:", err);
            // Set default settings if fetch fails
            setSettings({
              id: "",
              documentId: document._id || document.id,
              memberId: memberInfo.id,
              ownerId: document.memberId || memberInfo.id,
              availableOffline: false,
              isImportant: false,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
          })
          .finally(() => {
            setSettingsLoading(false);
          });
      }
    }
  }, [open, displayName, category, document]);

  const memberId = memberInfo.id;
  const loggedInMember = getMemberInfo(true);

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  // Consistent download logic with DocumentViewer
  const handleNativeDownload = async () => {
    if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
      try {
        triggerNativeDownload({
          fileKey: fileKey,
          filename: fileName || displayName,
        });
      } catch (e) {
        console.error("[DocumentDrawer] Error triggering native download", e);
      }
    } else if (fileKey && memberId) {
      try {
        const blob = await mediaService.download(memberId, fileKey);
        const objectUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        // Use displayName as filename, fallback to fileName/fileKey
        let downloadName = displayName || fileName || fileKey;
        // Try to preserve extension from fileName if present
        if (
          fileName &&
          displayName &&
          !displayName.endsWith(fileName.substring(fileName.lastIndexOf(".")))
        ) {
          const ext = fileName.substring(fileName.lastIndexOf("."));
          if (ext && !displayName.endsWith(ext))
            downloadName = displayName + ext;
        }
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
      } catch (e) {
        console.error("[DocumentDrawer] Error downloading file", e);
      }
    } else {
      console.warn("[DocumentDrawer] Download not available");
    }
  };

  // Minimal WebView-only share logic
  const handleNativeShare = async () => {
    if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
      try {
        triggerNativeShare({
          title: displayName,
          fileKey: fileKey,
          filename: fileName,
        });
      } catch (e) {
        console.error(
          "[DocumentDrawer] Error fetching signed URL for share",
          e
        );
      }
    } else if (onShareClick) {
      onShareClick();
    } else {
      console.warn("[DocumentDrawer] Share not available");
    }
  };

  // Flag update handlers
  const handleImportantToggle = async (checked: boolean) => {
    if (!document?._id && !document?.id) return;
    try {
      // Update local state immediately for real-time UI feedback
      setSettings((prev) =>
        prev
          ? { ...prev, isImportant: checked }
          : {
              id: "",
              documentId: document._id || document.id,
              memberId: memberInfo.id,
              ownerId: document.memberId || memberInfo.id,
              availableOffline: false,
              isImportant: checked,
              createdAt: new Date(),
              updatedAt: new Date(),
            }
      );

      await updateFlag(document._id || document.id, "isImportant", checked);

      // Update parent's document without triggering full refresh
      if (onDocumentChange && document) {
        onDocumentChange({
          ...document,
          settings: { ...settings, isImportant: checked },
        });
      }
      // Don't call onUpdate - keep drawer open and settings are already updated locally
    } catch (err) {
      console.error("Error updating isImportant flag:", err);
      // Revert local state on error
      setSettings((prev) => (prev ? { ...prev, isImportant: !checked } : null));
    }
  };

  const handlePrivateToggle = async (checked: boolean) => {
    if (!localDocument?._id && !localDocument?.id) return;
    try {
      // Update local state immediately for real-time UI feedback
      setLocalDocument((prev: any) => ({ ...prev, isPrivate: checked }));

      // Update isPrivate on the document itself, not in document_settings
      await updateDocumentFlag(localDocument, "isPrivate", checked);

      // Update parent's document without triggering full refresh
      if (onDocumentChange && document) {
        onDocumentChange({ ...document, isPrivate: checked });
      }
      // Don't call onUpdate - keep drawer open and document is already updated locally
    } catch (err) {
      console.error("Error updating isPrivate flag:", err);
      // Revert local state on error
      setLocalDocument((prev: any) => ({ ...prev, isPrivate: !checked }));
    }
  };

  const handleOfflineToggle = async (checked: boolean) => {
    if (!document?._id && !document?.id) return;

    // Update local state immediately for real-time UI feedback
    setSettings((prev) =>
      prev
        ? { ...prev, availableOffline: checked }
        : {
            id: "",
            documentId: document._id || document.id,
            memberId: memberInfo.id,
            ownerId: document.memberId || memberInfo.id,
            availableOffline: checked,
            isImportant: false,
            createdAt: new Date(),
            updatedAt: new Date(),
          }
    );

    // Trigger native bridge for offline storage
    if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
      triggerNativeOfflineDoc({
        fileKey,
        displayName,
        availableOffline: checked,
      });
    }

    try {
      await updateFlag(
        document._id || document.id,
        "availableOffline",
        checked
      );

      // Update parent's document without triggering full refresh
      if (onDocumentChange && document) {
        onDocumentChange({
          ...document,
          settings: { ...settings, availableOffline: checked },
        });
      }
      // Don't call onUpdate - keep drawer open and settings are already updated locally
    } catch (err) {
      console.error("Error updating availableOffline flag:", err);
      // Revert local state on error
      setSettings((prev) =>
        prev ? { ...prev, availableOffline: !checked } : null
      );
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!onDelete) return;
    setDeleteLoading(true);
    try {
      await onDelete();
      setDeleteDialogOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <FooterDrawer
      open={open}
      onClose={onClose}
      title="Document details"
      allowKeyboard
      height={
        document?.memberId !== loggedInMember.id
          ? category === FileCategory.EmergencyCard
            ? "36vh"
            : "70vh"
          : category === FileCategory.EmergencyCard
          ? "43vh"
          : "80vh"
      }
    >
      {/* Document Info Fields */}
      {category !== FileCategory.EmergencyCard && (
        <div
          style={{
            padding: "24px 24px 0px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {/* Document Name */}
          <MInputField
            label="Document name"
            value={editableDisplayName}
            placeholder="Not Answered"
            onChange={(e) => {
              setEditableDisplayName(e.target.value);
              setDisplayNameChanged(true);
            }}
            onBlur={async () => {
              if (displayNameChanged && editableDisplayName !== displayName) {
                try {
                  await updateUncategorizedDocument(document, {
                    displayName: editableDisplayName,
                    categorize: true,
                    ocrCorrection: true,
                  });
                  setDisplayNameChanged(false);
                  if (onUpdate) onUpdate();
                } catch (e) {
                  console.error("Failed to update document display name", e);
                }
              }
            }}
            isDrawerInput
          />
          {/* Document Date */}
          <MInputField
            label="Document date"
            value={
              editableDate
                ? editableDate.toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : ""
            }
            placeholder="Select date"
            readOnly
            showIcon
            icon={<IconCaretDownFilled />}
            onClick={() => setShowDatePicker(true)}
            isDrawerInput
          />
          <MDatePicker
            open={showDatePicker}
            onClose={() => setShowDatePicker(false)}
            title="Document date"
            initialValue={editableDate}
            maxDate={new Date()}
            onDone={async (date) => {
              setShowDatePicker(false);
              setEditableDate(date);
              try {
                await updateUncategorizedDocument(document, {
                  documentDate: date.toISOString(),
                });
                if (onDocumentChange && document) {
                  onDocumentChange({
                    ...document,
                    documentDate: date.toISOString(),
                  });
                }
              } catch (e) {
                console.error("Failed to update document date", e);
              }
            }}
          />
          {/* Document Owner Name */}
          <MInputField
            label="Document owner"
            value={
              assignedTo
                ? `${assignedTo.first_name || assignedTo.firstName || ""}${
                    assignedTo.last_name || assignedTo.lastName
                      ? " " + (assignedTo.last_name || assignedTo.lastName)
                      : ""
                  }`
                : ""
            }
            onClick={() => setShowMemberDrawer(true)}
            placeholder="Select owner"
            readOnly
            mandatory
            showIcon
            icon={<IconCaretDownFilled />}
            isDrawerInput
          />
          {/* Type of Document */}
          <MInputField
            label="Type"
            value={
              fileCategory !== null && FileCategoryLabel[fileCategory]
                ? FileCategoryLabel[fileCategory]
                : ""
            }
            onClick={() => setShowCategoryDrawer(true)}
            placeholder="Select type"
            readOnly
            mandatory
            showIcon
            icon={<IconCaretDownFilled />}
            isDrawerInput
          />
        </div>
      )}
      {/* Member Picker Drawer */}
      <MemberPickerDrawer
        open={showMemberDrawer}
        members={members}
        selectedId={assignedTo?.id}
        onSelect={async (member) => {
          setAssignedTo(member);
          setShowMemberDrawer(false);
          try {
            await updateUncategorizedDocument(document, {
              memberId: member.id,
              categorize: true,
              ocrCorrection: true,
            });
            if (onUpdate) onUpdate();
          } catch (e) {
            console.error("Failed to update document owner", e);
          }
        }}
        onClose={() => setShowMemberDrawer(false)}
        title="Select Owner"
      />
      {/* Category Dropdown Drawer */}
      <MDropDownDrawer
        open={showCategoryDrawer}
        title="Select document type"
        items={Object.values(FileCategory)
          .filter((v) => typeof v === "number" && v !== FileCategory.Other)
          .map((v) => ({
            value: String(v),
            label: FileCategoryLabel[v as FileCategory],
          }))}
        initialValue={fileCategory !== null ? String(fileCategory) : ""}
        onSelect={async (value) => {
          const newCategory = Number(value) as FileCategory;
          setFileCategory(newCategory);
          setShowCategoryDrawer(false);
          try {
            await updateUncategorizedDocument(document, {
              category: newCategory,
              ocrCorrection: true,
              categorize: true,
            });
            if (onUpdate) onUpdate();
          } catch (e) {
            console.error("Failed to update document category", e);
          }
        }}
        onClose={() => setShowCategoryDrawer(false)}
      />
      {/* Delete Confirmation Dialog */}
      <MDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete record?"
        description="This record will be permanently deleted."
        primaryCtaText="Yes"
        secondaryCtaText="No"
        onPrimaryCta={handleDeleteConfirm}
        onSecondaryCta={() => setDeleteDialogOpen(false)}
        isPrimaryCtaLoading={deleteLoading}
      />
      <div className={styles.drawerContentStyled}>
        {!isDesktopPWA && (
          <div
            className={styles.drawerActionCard}
            onClick={handleNativeShare}
            style={{
              cursor: "pointer",
              opacity: 1,
            }}
          >
            <IconShare3 size={28} className={styles.drawerActionIcon} />
            <span className={styles.drawerActionText}>Share</span>
          </div>
        )}
        <div
          className={styles.drawerActionCard}
          onClick={onCommentClick}
          style={{ cursor: "pointer" }}
        >
          <IconMessage2 size={28} className={styles.drawerActionIcon} />
          <span className={styles.drawerActionText}>{commentButtonText}</span>
        </div>
      </div>
      <div className={styles.drawerList}>
        {/* <MNavigationButton
          key={"download"}
          leftIcon={<IconDownload />}
          text={"Download"}
          className={styles.navigationStyle}
          onClick={handleNativeDownload}
        /> */}
        <div className="flex gap-2 w-full">
          <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border border-solid border-[#e2e2e2] bg-white px-4 py-2 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-center gap-1">
              <IconStar size={16} className="shrink-0" />
              <span className="whitespace-nowrap text-xs font-semibold tracking-[0.12px] text-[#474747]">
                Favourite
              </span>
            </div>
            <MToggle
              checked={settings?.isImportant || false}
              onChange={handleImportantToggle}
            />
          </div>
          {!isDesktopPWA && category !== FileCategory.EmergencyCard && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border border-solid border-[#e2e2e2] bg-white px-4 py-2 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-center gap-1">
                <IconCloudDownload size={16} className="shrink-0" />
                <span className="whitespace-nowrap text-xs font-semibold tracking-[0.12px] text-[#474747]">
                  Offline
                </span>
              </div>
              <MToggle
                checked={settings?.availableOffline || false}
                onChange={handleOfflineToggle}
              />
            </div>
          )}
          {document?.memberId === loggedInMember.id && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border border-solid border-[#e2e2e2] bg-white px-4 py-2 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-center gap-1">
                <IconShieldLock size={16} className="shrink-0" />
                <span className="whitespace-nowrap text-xs font-semibold tracking-[0.12px] text-[#474747]">
                  Private
                </span>
              </div>
              <MToggle
                checked={localDocument?.isPrivate || false}
                onChange={handlePrivateToggle}
              />
            </div>
          )}
        </div>
        {category !== FileCategory.EmergencyCard && (
          <MNavigationButton
            leftIcon={<IconTrash />}
            text="Delete"
            className={styles.deleteButton}
            onClick={handleDeleteClick}
            variant="delete"
          />
        )}
      </div>
    </FooterDrawer>
  );
};

export default DocumentDrawer;
